import type { FloorAssemblyHashParameters } from "building-types/floors/floorAssemblyTypes";
import type { FloorPath } from "building-types/floors/floorTypes";
import type { FoundationComponentEntity } from "building-types/foundations/foundationComponentEntityTypes";
import { BrickLedgeType } from "building-types/foundations/foundationEnums";
import type { FoundationExtrusionSize, FoundationPath, Slab } from "building-types/foundations/foundationTypes";
import type { EdgeData } from "building-types/provenance/halfEdgeDataTypes";
import { RoomClass } from "building-types/rooms/roomTypesEnum";
import type { SlabSettings } from "building-types/settings/foundationSettingsTypes";
import { PathPriority } from "building-types/walls/pathPriority";
import type { Table } from "common/data/table";
import uid from "common/utils/uid";
import { faceNew } from "geom/brep";
import { extrudeFace } from "geom/extrude";
import { planeNewXY } from "geom/plane";
import { polygonReverseCopy } from "geom/polyLine2Polygon";
import type { PolyLine2Polygon } from "geom/polyLine2Types";
import { floorPathTop } from "../floor/floorPathUtils";
import { brickLedgeTypeAtCondition } from "./brickLedge";
import { buildFoundationAssemblyComponents } from "./foundationAssemblyTools";
import { foundationExtrusionSizeNew } from "./foundationStrip";
import { slabDepthAtFoundationPath, slabDepthAtRoomClass } from "./slabUtils";
import { stemWallBrickLedgeWidth, stemWallTop, stemWallWidth } from "./stemWall";

export function slabNew({
  size,
  roomClass,
  halfEdges,
  planProfile,
  exteriorProfile,
  planProfileHalfEdges,
  exteriorProfileHalfEdges,
  hashParams,
}: {
  size: FoundationExtrusionSize;
  roomClass: RoomClass;
  halfEdges: number[];
  planProfile: PolyLine2Polygon;
  exteriorProfile: PolyLine2Polygon;
  planProfileHalfEdges: EdgeData[][][];
  exteriorProfileHalfEdges: EdgeData[][][];
  hashParams: Partial<FloorAssemblyHashParameters>;
}): Slab {
  const { top, depth } = size;
  const slabPlane = planeNewXY(0, 0, top);
  const slabFace = faceNew(polygonReverseCopy(planProfile), slabPlane);
  return {
    id: uid(),
    size,
    roomClass,
    halfEdges,
    planProfile,
    exteriorProfile,
    extrusion: extrudeFace(slabFace, 0, 0, -depth),
    isSlabDepression: false,
    planProfileHalfEdges,
    exteriorProfileHalfEdges,
    hashParams,
  };
}

export function slabEdgeSubSystemOffset(
  roomClass: RoomClass,
  foundationPath: FoundationPath,
  floorPath: FloorPath,
  slabSettings: SlabSettings,
  foundationComponents: Table<FoundationComponentEntity>,
) {
  const { assembly, superSystemOffset, subSystemOffset } = foundationPath;
  const { leftRoomClass, rightRoomClass, leftRoom, rightRoom } = floorPath;

  let offset = superSystemOffset;

  // easy out if this is not a priority edge
  if (leftRoomClass >= rightRoomClass) {
    return offset;
  }

  const { stemWall } = buildFoundationAssemblyComponents(foundationComponents, assembly);

  const slabLevel = leftRoom?.level ?? rightRoom?.level;
  const slabTop = floorPathTop(slabLevel, floorPath);
  const slabDepth = slabDepthAtFoundationPath({ condition: foundationPath.condition }, slabSettings);
  const slabBottom = slabTop - slabDepth;

  if (stemWall) {
    const stemTop = stemWallTop({
      foundationPath,
      stemWall,
      level: slabLevel,
      slabSettings,
    });
    if (stemTop > slabBottom) {
      offset = subSystemOffset;
      const { width: additionalOffset } = stemWallWidth({ path: foundationPath, stemWall });
      offset -= additionalOffset;
    }
  }

  if (roomClass === RoomClass.PORCH && leftRoomClass !== rightRoomClass) {
    offset += slabSettings.porchSlabOverhang;
  }

  return offset;
}

export function slabEdgeSubSystemExteriorOffset(
  _: RoomClass,
  foundationPath: FoundationPath,
  floorPath: FloorPath,
  __: SlabSettings,
  foundationComponents: Table<FoundationComponentEntity>,
) {
  const { assembly, superSystemOffset, subSystemOffset, condition } = foundationPath;
  const { floorPriority } = floorPath;

  let offset = superSystemOffset;

  // easy out if this is not a priority edge
  if (floorPriority === PathPriority.NONE) {
    return offset;
  }

  const { stemWall } = buildFoundationAssemblyComponents(foundationComponents, assembly);

  if (stemWall) {
    if (
      brickLedgeTypeAtCondition(condition, stemWall.brickLedgeType, stemWall.brickLedgeBehavior) ===
      BrickLedgeType.INSET
    ) {
      offset = subSystemOffset;
      const brickLedgeWidth = stemWallBrickLedgeWidth({ path: foundationPath, stemWall });
      if (floorPriority === PathPriority.RIGHT) {
        offset -= brickLedgeWidth;
      } else if (floorPriority === PathPriority.LEFT) {
        offset += brickLedgeWidth;
      }
    }
  }

  return offset;
}

export function makeSlabSize({
  roomClass,
  slabSettings,
  floorElevation,
}: {
  roomClass: RoomClass;
  slabSettings: SlabSettings;
  floorElevation: number;
}): FoundationExtrusionSize {
  const slabTop = floorElevation;
  const slabDepth = slabDepthAtRoomClass(roomClass, slabSettings);
  return foundationExtrusionSizeNew(slabTop, slabDepth);
}
